import { AutoComplete, Input, Radio, Switch } from '@doopage/react-ui-kit';
import { t } from '@lingui/macro';
import { createPaymentChannel, startPaymentLink } from 'api/api';
import BaseModal from 'component/BaseComponent/BaseModal';
import { handleApi } from 'helper/helper';
import Button from 'MUI_Kit/components/CustomButtons/Button';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { getAllChannels } from 'redux/action/apiAction';
import Routes from 'router/routes';
import DV from 'variables/DV';
import {
  handlePaymentLinkProgress,
  LINKED_BANK,
  MANUAL_BANK,
  toTransferContent,
  usePaymentChannelBanks,
  usePaymentProviders,
} from './utils';

const ACCOUNT_NUMBER_REGEX = /^[0-9]{6,20}$/;

// Whether the owner already has an account at the provider. Without one they sign up
// first -- through our referral link -- then come back and connect.
const HAS_ACCOUNT = 'has_account';
const NO_ACCOUNT = 'no_account';

const AccountChoiceRow = ({ title, hint, selected, disabled, onClick }) => (
  <div
    className={`flex items-center rounded-lg border px-[14px] py-[10px] cursor-pointer ${
      selected ? 'border-primary bg-gray-50' : 'border-gray-200'
    }`}
    onClick={disabled ? undefined : onClick}>
    <div className="flex-1">
      <div className="font-bold">{title}</div>
      <div className="text-sm opacity-60">{hint}</div>
    </div>
    <Radio checked={selected} disabled={disabled} />
  </div>
);

const Content = forwardRef(({ onStateChange, closeModal }, ref) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { data: banks, loading: banksLoading, error: banksError } = usePaymentChannelBanks();
  const { data: providers } = usePaymentProviders();

  const [notifyEnabled, setNotifyEnabled] = useState(false);
  // With notifications on it holds either a bank linked directly (its code, e.g. "ACB")
  // or a provider (its subtype, e.g. "sepay"); the account then comes from the link
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountChoice, setAccountChoice] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Banks DooPage links with directly, as the API reports them
  const directBanks = useMemo(() => banks.filter((bank) => bank.is_support_link), [banks]);

  // What the pick links through while notifications are on, if anything
  const linkTarget = useMemo(() => {
    if (!notifyEnabled || !bankCode) return undefined;
    const provider = providers.find((item) => item.subtype === bankCode);
    if (provider)
      return { name: provider.name, subtype: provider.subtype, signupUrl: provider.signup_url };
    const bank = directBanks.find((item) => item.code === bankCode);
    if (bank) return { name: bank.short_name, subtype: LINKED_BANK, bankCode: bank.code };
    return undefined;
  }, [notifyEnabled, bankCode, providers, directBanks]);
  // No sign-up link means linking needs no account of the owner's own
  const needsAccountChoice = !!linkTarget?.signupUrl;
  const isSignup = needsAccountChoice && accountChoice === NO_ACCOUNT;

  // Notifications on: what can report transfers, grouped as banks linked directly and
  // providers in between -- grouped only once there is a bank to set apart.
  // Notifications off: every bank, typed in by hand.
  const bankOptions = useMemo(() => {
    if (notifyEnabled) {
      const grouped = directBanks.length > 0;
      return [
        ...directBanks.map((bank) => ({
          id: bank.code,
          name: bank.short_name,
          description: t`Liên kết trực tiếp với ngân hàng`,
          avatar: bank.logo_url,
          group: t`Ngân hàng liên kết trực tiếp`,
        })),
        ...providers.map((item) => ({
          id: item.subtype,
          name: item.name,
          description: item.description,
          avatar: item.logo_url,
          group: grouped ? t`Qua đơn vị trung gian` : undefined,
        })),
      ];
    }
    return banks.map((bank) => ({
      id: bank.code,
      name: bank.short_name,
      description: bank.full_name,
      avatar: bank.logo_url,
    }));
  }, [banks, directBanks, providers, notifyEnabled]);

  // Keep the pick in step with the switch: a lone option is picked for the owner,
  // and a bank picked before no longer applies
  useEffect(() => {
    if (bankOptions.length === 1) {
      setBankCode(bankOptions[0].id);
      return;
    }
    if (bankCode && !bankOptions.some((option) => option.id === bankCode)) setBankCode('');
  }, [bankOptions]);

  // Every provider asks its own question about the account, so start fresh on a new pick
  useEffect(() => {
    setAccountChoice(null);
  }, [bankCode]);

  const errors = useMemo(() => {
    const result = {};
    if (!bankCode) result.bankCode = t`Vui lòng chọn ngân hàng`;
    if (notifyEnabled) return result;
    if (!ACCOUNT_NUMBER_REGEX.test(accountNumber))
      result.accountNumber = t`Số tài khoản gồm 6-20 chữ số`;
    if (!accountName.trim()) result.accountName = t`Vui lòng nhập tên chủ tài khoản`;
    return result;
  }, [bankCode, notifyEnabled, accountNumber, accountName]);
  const isValid = Object.keys(errors).length === 0;
  const canSubmit =
    isValid &&
    !submitting &&
    !(notifyEnabled && (!linkTarget || (needsAccountChoice && !accountChoice)));

  const submitLabel = !linkTarget
    ? t`Hoàn tất`
    : isSignup
    ? t`Đăng ký ${linkTarget.name}`
    : t`Kết nối ${linkTarget.name}`;

  useEffect(() => {
    onStateChange({ canSubmit, submitting, submitLabel });
  }, [canSubmit, submitting, submitLabel]);

  const showError = (error) => {
    if (
      typeof error === 'string' &&
      error.includes(t`nâng cấp gói cước`) &&
      !DV.company?.is_shared_doopage_to_facebook
    ) {
      DV.showWarningLimitModal();
      return;
    }
    setSubmitError(error || t`Có lỗi xảy ra, vui lòng thử lại`);
  };

  const openSignup = (signupUrl) => {
    // A new tab: signing up and linking a bank at the provider can take a while, and
    // this form is waiting for the owner when they come back
    window.open(signupUrl, '_blank', 'noopener');
    setAccountChoice(HAS_ACCOUNT);
  };

  const startLink = async ({ subtype, bankCode: linkBankCode }) => {
    const { success, data, error } = await handleApi(
      startPaymentLink({
        subtype,
        // A direct bank link has to say which bank; a provider covers them all
        params: linkBankCode ? { bank_code: linkBankCode } : undefined,
      }),
    );
    if (!success) return showError(error);
    // The select step takes this modal's place; anything else happens on the channel page
    if (data?.step?.kind !== 'select') closeModal();
    handlePaymentLinkProgress(data, { history, dispatch, replace: true });
  };

  const createManualBank = async () => {
    const { success, error } = await handleApi(
      createPaymentChannel({
        bank_code: bankCode,
        account_number: accountNumber,
        account_name: accountName.trim(),
        subtype: MANUAL_BANK,
      }),
    );
    if (!success) return showError(error);
    DV.showNotify(t`Thêm kênh thanh toán thành công`);
    dispatch(getAllChannels());
    closeModal();
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      setTouched({ bankCode: true, accountNumber: true, accountName: true });
      if (!canSubmit) return;
      setSubmitError(null);
      setSubmitting(true);
      if (notifyEnabled) {
        if (isSignup) openSignup(linkTarget.signupUrl);
        else await startLink(linkTarget);
      } else {
        await createManualBank();
      }
      setSubmitting(false);
    },
  }));

  const touch = (name) => () => setTouched((value) => ({ ...value, [name]: true }));

  return (
    <div className="flex flex-col gap-[16px]">
      <div className="opacity-70">
        {t`Thêm tài khoản ngân hàng để gửi yêu cầu thanh toán (mã QR chuyển khoản) cho khách hàng.`}
      </div>

      {/* Only offered when something can report transfers */}
      {(providers.length > 0 || directBanks.length > 0 || notifyEnabled) && (
        <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-[14px] py-[10px]">
          <div className="flex-1">
            <div className="font-bold">{t`Nhận thông báo giao dịch`}</div>
            <div className="text-sm opacity-60">
              {t`Tự động xác nhận thanh toán khi có tiền vào tài khoản.`}
            </div>
          </div>
          <Switch
            checked={notifyEnabled}
            disabled={submitting}
            onChange={(e) => setNotifyEnabled(e.target.checked)}
          />
        </div>
      )}

      <div>
        <AutoComplete
          key={`${notifyEnabled}-${bankOptions.length}`}
          fullWidth
          disableClearable
          label={notifyEnabled ? t`Kết nối qua` : t`Ngân hàng`}
          placeholder={notifyEnabled ? t`Chọn ngân hàng hoặc đơn vị kết nối` : t`Chọn ngân hàng`}
          options={bankOptions}
          groupBy={notifyEnabled ? (option) => option.group || '' : undefined}
          loading={!notifyEnabled && banksLoading}
          disabled={submitting}
          value={bankCode || null}
          onChange={(value) => {
            setBankCode(value);
            touch('bankCode')();
          }}
          error={
            (touched.bankCode && errors.bankCode) ||
            (!notifyEnabled && banksError ? t`Không tải được danh sách ngân hàng` : undefined)
          }
          helperText={
            notifyEnabled ? t`Tài khoản nhận tiền sẽ được chọn sau khi bạn cấp quyền.` : undefined
          }
        />
      </div>

      {notifyEnabled ? (
        // The account is picked from the bank or provider after authorising
        linkTarget && (
          <>
            {needsAccountChoice && (
              <div className="flex flex-col gap-[8px]">
                <div className="font-bold">{t`Bạn đã có tài khoản ${linkTarget.name} chưa?`}</div>
                <AccountChoiceRow
                  title={t`Đã có tài khoản`}
                  hint={t`Đăng nhập ${linkTarget.name} và chọn tài khoản ngân hàng nhận tiền.`}
                  selected={accountChoice === HAS_ACCOUNT}
                  disabled={submitting}
                  onClick={() => setAccountChoice(HAS_ACCOUNT)}
                />
                <AccountChoiceRow
                  title={t`Chưa có tài khoản`}
                  hint={t`Đăng ký ${linkTarget.name} và liên kết ngân hàng trước, sau đó quay lại để kết nối.`}
                  selected={accountChoice === NO_ACCOUNT}
                  disabled={submitting}
                  onClick={() => setAccountChoice(NO_ACCOUNT)}
                />
              </div>
            )}
            {(!needsAccountChoice || accountChoice) && (
              <div className="rounded-lg bg-gray-50 px-[14px] py-[10px] opacity-80">
                {isSignup
                  ? t`Trang đăng ký ${linkTarget.name} sẽ mở ở tab mới. Sau khi đăng ký và liên kết ngân hàng, quay lại đây và chọn "Kết nối ${linkTarget.name}".`
                  : linkTarget.bankCode
                  ? t`Bạn sẽ được chuyển sang ${linkTarget.name} để cấp quyền, sau đó chọn tài khoản nhận tiền.`
                  : t`Bạn sẽ được chuyển sang ${linkTarget.name} để đăng nhập và cấp quyền, sau đó chọn tài khoản ngân hàng nhận tiền.`}
              </div>
            )}
          </>
        )
      ) : (
        <>
          <Input
            fullWidth
            label={t`Số tài khoản`}
            placeholder={t`Nhập số tài khoản`}
            value={accountNumber}
            disabled={submitting}
            inputMode="numeric"
            onChange={(e) => setAccountNumber(e.target.value.replace(/[^0-9]/g, ''))}
            onBlur={touch('accountNumber')}
            error={touched.accountNumber ? errors.accountNumber : undefined}
          />
          <Input
            fullWidth
            label={t`Tên chủ tài khoản`}
            placeholder={t`VD: NGUYEN VAN A`}
            value={accountName}
            disabled={submitting}
            // Banks print the account holder name without diacritics, so the value is
            // coerced as it is typed rather than validated after the fact
            onChange={(e) => setAccountName(toTransferContent(e.target.value))}
            onBlur={touch('accountName')}
            error={touched.accountName ? errors.accountName : undefined}
          />
        </>
      )}

      {!!submitError && <div className="text-danger">{String(submitError)}</div>}
    </div>
  );
});

class AddPaymentChannelModal extends BaseModal {
  constructor(props) {
    super(props);
    this.state = {
      open: true,
      title: t`Thêm kênh thanh toán`,
      canSubmit: false,
      submitting: false,
      submitLabel: t`Hoàn tất`,
    };
    this.goPathWhenClose = Routes.channel.path;
    this.content = React.createRef();
  }

  renderBody() {
    return (
      <Content
        ref={this.content}
        closeModal={() => this.close()}
        onStateChange={(value) => this.setState(value)}
      />
    );
  }

  renderFooter() {
    const { canSubmit, submitting, submitLabel } = this.state;
    return (
      <Button
        loading={submitting}
        disabled={!canSubmit}
        onClick={() => this.content.current?.submit()}
        color="success">
        {submitLabel}
      </Button>
    );
  }
}

export default connect()(AddPaymentChannelModal);
